import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { WorkflowInput, WorkflowTemplate } from "@sales-ops-amplify/shared";
import {
  useAiDraftWorkflow,
  useCreateWorkflow,
  useWorkflowTemplates,
} from "@/hooks/useWorkflows";
import { cn } from "@/cn";

type Mode = "choose" | "ai" | "template" | "scratch";

export function CreateWorkflowModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("choose");
  const [name, setName] = useState("Untitled workflow");
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState<WorkflowInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const templatesQuery = useWorkflowTemplates();
  const createMut = useCreateWorkflow();
  const aiMut = useAiDraftWorkflow();

  async function createFrom(input: WorkflowInput & { templateId?: string; aiPrompt?: string }) {
    setError(null);
    try {
      const res = await createMut.mutateAsync({
        ...input,
        name: name.trim() || input.name || "Untitled workflow",
        status: "draft",
      });
      onClose();
      navigate(`/workflows/${res.workflow.workflowId}/edit`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    }
  }

  async function runAi() {
    setError(null);
    try {
      const res = await aiMut.mutateAsync(prompt);
      setDraft(res.draft);
      if (res.draft.name) setName(res.draft.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : "AI draft failed");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-border bg-background p-5 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Create a workflow</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </div>

        {mode === "choose" && (
          <div className="grid gap-2">
            {(
              [
                ["ai", "AI assistant", "Describe a goal in plain language"],
                ["template", "From a template", "Start from a proven motion"],
                ["scratch", "From scratch", "Build trigger and actions yourself"],
              ] as const
            ).map(([id, title, desc]) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className="rounded-lg border border-border px-4 py-3 text-left hover:bg-muted/40"
              >
                <p className="text-sm font-medium">{title}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </button>
            ))}
          </div>
        )}

        {mode !== "choose" && (
          <label className="block text-sm">
            <span className="text-muted-foreground">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
            />
          </label>
        )}

        {mode === "ai" && (
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="text-muted-foreground">Describe your workflow</span>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                placeholder="Enroll new contacts into a sequence and create a task"
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </label>
            <button
              type="button"
              disabled={aiMut.isPending || !prompt.trim()}
              onClick={() => void runAi()}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-60"
            >
              {aiMut.isPending ? "Generating…" : "Generate draft"}
            </button>
            {draft && (
              <div className="rounded-md border border-border p-3 text-xs text-muted-foreground">
                Trigger: {draft.trigger?.type} · {draft.nodes?.length ?? 0} steps
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setMode("choose")} className="text-sm">
                Back
              </button>
              <button
                type="button"
                disabled={createMut.isPending || !draft}
                onClick={() =>
                  draft &&
                  void createFrom({ ...draft, aiPrompt: prompt })
                }
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-60"
              >
                Create draft
              </button>
            </div>
          </div>
        )}

        {mode === "template" && (
          <div className="space-y-3">
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {(templatesQuery.data?.templates ?? []).map((t: WorkflowTemplate) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() =>
                    void createFrom({
                      ...t.draft,
                      name: name.trim() || t.name,
                      templateId: t.id,
                    })
                  }
                  className={cn(
                    "w-full rounded-lg border border-border px-3 py-2.5 text-left hover:bg-muted/40",
                  )}
                >
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.description}</p>
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setMode("choose")} className="text-sm">
              Back
            </button>
          </div>
        )}

        {mode === "scratch" && (
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setMode("choose")} className="text-sm">
              Back
            </button>
            <button
              type="button"
              disabled={createMut.isPending}
              onClick={() =>
                void createFrom({
                  name,
                  trigger: { type: "manual" },
                  filterRules: [],
                  nodes: [],
                  entryNodeId: null,
                  targetType: "people",
                })
              }
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-60"
            >
              Continue to builder
            </button>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </div>
  );
}
